import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { ProgramParams, createProgramParams } from './ProgramParams';
import { MesoCycle, createMesoCycle } from './MesoCycle';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { MicroCycle, createMicroCycle } from './MicroCycle';
import { Athlete } from '../../Athlete/Athlete';
import { EquipmentConfiguration } from './EquipmentConfiguration';
import { ProgramSchedule, createProgramSchedule } from '../ProgramSchedule';
import { Audited, createAuditedFields } from '../../Audited';
import { FitnessProgramTemplate, FitnessProgramTemplateApi } from './Template/FitnessProgramTemplate';
import { CoachProgramType } from './CoachProgramType';
import { SuperMacro, SuperMacroApi } from '../SuperMacro/SuperMacro';
import { Pageable } from '../../Interfaces/Pageable';

export type CoachProgramQueryRequest = Pageable & {
  athleteId?: string;
  tags?: string[];
  excludedTags?: string[];
  active?: boolean;
};

export type CoachProgram = Audited & {
  athleteId: string;
  schedule: ProgramSchedule;
  superMacroTemplateId: string;
  currentParams: ProgramParams;
  currentMacroCycleLaneId?: string;
  currentMesoCycle?: MesoCycle;
  startDateTime?: Date;
  endDateTime?: Date;
  type: CoachProgramType;
  template?: FitnessProgramTemplate | SuperMacro;
  athlete?: Athlete;
  readonly currentWeekIndex: number | undefined;
  changeParams(params?: ProgramParams): Promise<MicroCycle>;
  changeSchedule(schedule: ProgramSchedule): Promise<CoachProgram>;
  changeEquipment(equipment: EquipmentConfiguration): Promise<MicroCycle>;
  startCycle(): Promise<MicroCycle>;
  end(): Promise<void>;
  refreshCurrentMicroCycle(params?: ProgramParams): Promise<any>;
};

export function createCoachProgram(json?: Partial<CoachProgram>): CoachProgram {
  const audited = createAuditedFields(json);

  const program: CoachProgram = makeAutoObservable({
    ...audited,
    athleteId: json?.athleteId ?? '',
    schedule: createProgramSchedule(json?.schedule as any),
    superMacroTemplateId: json?.superMacroTemplateId ?? '',
    currentParams: createProgramParams(json?.currentParams),
    currentMacroCycleLaneId: json?.currentMacroCycleLaneId,
    currentMesoCycle: json?.currentMesoCycle ? createMesoCycle(json.currentMesoCycle as any) : undefined,
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : undefined,
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : undefined,
    type: json?.type ?? 'superMacro',
    template: undefined as FitnessProgramTemplate | SuperMacro | undefined,
    athlete: undefined as Athlete | undefined,

    get currentWeekIndex(): number | undefined {
      const template = this.template as FitnessProgramTemplate | undefined;
      if (template && 'mesoCycles' in template) {
        if (this.currentMesoCycle?.configuration.mesoCycleTemplateId) {
          const currentCycleIndex = template.mesoCycles.findIndex(
            (cycle) => cycle.id === this.currentMesoCycle?.configuration.mesoCycleTemplateId,
          );
          const startIndex = Math.max(
            0,
            this.currentMesoCycle.microCycleIds.findIndex((id) => id === this.currentMesoCycle?.currentMicroCycleId),
          );

          return template.mesoCycles.reduce(
            (total, cycle, index) => (index < currentCycleIndex ? total + cycle.cycles : total),
            startIndex,
          );
        }
        return 0;
      }
      return undefined;
    },

    changeParams(params?: ProgramParams): Promise<MicroCycle> {
      this.currentParams = params ?? this.currentParams;
      return HttpBackend.post(`/coach/${this.id}/changeParams`, this.currentParams).then((result) =>
        createMicroCycle(result),
      );
    },

    changeSchedule(schedule: ProgramSchedule): Promise<CoachProgram> {
      this.schedule = schedule;
      if (this.type === 'program') {
        return HttpBackend.post(`/coach/program/${this.id}/changeSchedule`, schedule).then(() => this);
      }
      return HttpBackend.post(`/coach/${this.id}/changeSchedule`, schedule).then(() => this);
    },

    changeEquipment(equipment: EquipmentConfiguration): Promise<MicroCycle> {
      this.currentParams.equipmentConfiguration = equipment;
      if (this.type === 'program') {
        return HttpBackend.post(`/coach/program/${this.id}/changeEquipmentConfiguration`, equipment).then(
          (result) => createMicroCycle(result),
        );
      }
      return HttpBackend.post(`/coach/${this.id}/changeEquipmentConfiguration`, equipment).then((result) =>
        createMicroCycle(result),
      );
    },

    startCycle(): Promise<MicroCycle> {
      if (this.type === 'program') {
        return HttpBackend.post(`/coach/program/${this.id}/startCycle`)
          .then((result) => createMicroCycle(result))
          .then((microCycle) => {
            if (this.currentMesoCycle) {
              this.currentMesoCycle.microCycleIds.push(microCycle.id);
            }
            return microCycle;
          });
      }

      return HttpBackend.post(`/coach/${this.id}/startCycle`)
        .then((result) => createMicroCycle(result))
        .then((microCycle) => {
          if (this.currentMesoCycle) {
            this.currentMesoCycle.microCycleIds.push(microCycle.id);
          }
          return microCycle;
        });
    },

    end(): Promise<void> {
      if (this.type === 'superMacro') {
        return HttpBackend.delete(`/coach/${this.id}`);
      }
      return HttpBackend.delete(`/coach/program/${this.id}`);
    },

    refreshCurrentMicroCycle(params?: ProgramParams) {
      return this.type === 'program'
        ? HttpBackend.post(`/coach/program/${this.id}/refreshCurrentMicroCycle`, params)
        : HttpBackend.post(`/coach/${this.id}/refreshCurrentMicroCycle`, params);
    },
  });

  return program;
}

export const CoachProgramApi = {
  currentlyActive(): Promise<CoachProgram[]> {
    return HttpBackend.get('/coach/program/currentlyActive').then((result) =>
      result.map((t: any) => createCoachProgram(t)),
    );
  },

  find(params?: CoachProgramQueryRequest): Promise<CoachProgram[]> {
    return HttpBackend.get('/coach', params).then((result) => result.map((t: any) => createCoachProgram(t)));
  },

  get(id: string): Promise<CoachProgram> {
    return HttpBackend.get(`/coach/${id}`).then((result) => createCoachProgram(result));
  },
};
